import {
  Connection,
  PublicKey,
  Transaction,
  TransactionInstruction,
} from '@solana/web3.js';
import * as SPLToken from '@solana/spl-token';
import { BN } from '@coral-xyz/anchor';

const TOKEN_PROGRAM_ID = SPLToken.TOKEN_PROGRAM_ID;
const ASSOCIATED_TOKEN_PROGRAM_ID = SPLToken.ASSOCIATED_TOKEN_PROGRAM_ID;

/**
 * SPL Token utilities for Solana
 * Handles SPL token operations similar to ERC20 on BNB
 */

export interface SPLTokenInfo {
  mint: PublicKey;
  symbol: string;
  name: string;
  decimals: number;
  supply?: number;
  balance?: number;
  logo?: string;
}

export class SolanaSPLToken {
  private connection: Connection;

  constructor(connection: Connection) {
    this.connection = connection;
  }

  /**
   * Get SPL token info by mint address
   */
  async getTokenInfo(mintAddress: PublicKey): Promise<SPLTokenInfo | null> {
    try {
      const mintInfo = await this.connection.getParsedAccountInfo(mintAddress);

      if (
        !mintInfo.value ||
        !mintInfo.value.data ||
        'parsed' in mintInfo.value.data === false
      ) {
        return null;
      }

      const parsedData = mintInfo.value.data as any;
      const mintData = parsedData.parsed.info;
      const decimals = mintData.decimals || 0;
      const supply = mintData.supply ? parseInt(mintData.supply) : undefined;

      // Try to get token metadata from API services
      let symbol = 'UNKNOWN';
      let name = 'Unknown Token';
      let logo: string | undefined;

      try {
        const mintAddressStr = mintAddress.toString();
        
        // Try multiple API sources for token metadata
        // 1. Try Jupiter Token List (most reliable)
        try {
          const jupiterListResponse = await fetch(
            'https://token.jup.ag/all',
            { 
              method: 'GET',
              headers: { 'Accept': 'application/json' }
            }
          );
          if (jupiterListResponse.ok) {
            const jupiterList = await jupiterListResponse.json();
            const token = jupiterList.find(
              (t: any) => t.address === mintAddressStr || t.address?.toLowerCase() === mintAddressStr.toLowerCase(),
            );
            if (token) {
              if (token.symbol && token.symbol !== 'UNKNOWN') {
                symbol = token.symbol;
              }
              if (token.name && token.name !== 'Unknown Token') {
                name = token.name;
              }
              if (token.logoURI) {
                logo = token.logoURI;
              }
            }
          }
        } catch (e) {
          console.warn('Jupiter API failed:', e);
        }

        // 2. If still UNKNOWN, try Solana Token List API
        if (symbol === 'UNKNOWN' || !logo) {
          try {
            const tokenListResponse = await fetch(
              `https://token-list-api.solana.cloud/v1/search?query=${mintAddressStr}`,
              { 
                method: 'GET',
                headers: { 'Accept': 'application/json' }
              }
            );
            if (tokenListResponse.ok) {
              const tokenListData = await tokenListResponse.json();
              if (tokenListData?.content?.length > 0) {
                const tokenInfo = tokenListData.content[0];
                if (tokenInfo.symbol && symbol === 'UNKNOWN') {
                  symbol = tokenInfo.symbol;
                }
                if (tokenInfo.name && name === 'Unknown Token') {
                  name = tokenInfo.name;
                }
                if (tokenInfo.logoURI && !logo) {
                  logo = tokenInfo.logoURI;
                }
              }
            }
          } catch (e) {
            console.warn('Solana Token List API failed:', e);
          }
        }

        // 3. Try CoinGecko Solana token list as last resort
        if (symbol === 'UNKNOWN' || !logo) {
          try {
            // CoinGecko doesn't have direct address lookup, skip for now
            // Could use their API with symbol search but requires symbol first
          } catch (e) {
            // Skip
          }
        }
      } catch (apiError) {
        // If all API calls fail, use a shortened address as fallback
        console.warn('Failed to fetch token metadata from API:', apiError);
      }

      // Final fallback: use shortened address if still UNKNOWN
      if (symbol === 'UNKNOWN') {
        const addressStr = mintAddress.toString();
        symbol = addressStr.slice(0, 4) + '...' + addressStr.slice(-4);
        name = `Token ${addressStr.slice(0, 8)}`;
      }

      return {
        mint: mintAddress,
        symbol,
        name,
        decimals,
        supply,
        logo,
      };
    } catch (error) {
      console.error('Failed to get token info:', error);
      return null;
    }
  }

  /**
   * Get user's SPL token balance
   */
  async getTokenBalance(
    userAddress: PublicKey,
    mintAddress: PublicKey,
  ): Promise<number> {
    try {
      // Query token accounts - this will fail if mint is invalid
      const tokenAccounts = await this.connection.getParsedTokenAccountsByOwner(
        userAddress,
        { mint: mintAddress },
      );

      if (tokenAccounts.value.length === 0) {
        return 0;
      }

      const tokenAccount = tokenAccounts.value[0];
      const balance =
        tokenAccount.account.data.parsed.info.tokenAmount.uiAmount;
      return balance || 0;
    } catch (error: any) {
      // Handle specific error cases for invalid mint addresses
      const errorMessage = error?.message || error?.toString() || '';
      const errorName = error?.name || error?.constructor?.name || '';
      
      // Check for invalid mint address errors
      const isInvalidMintError =
        errorMessage.includes('could not be unpacked') ||
        errorMessage.includes('Invalid param') ||
        errorMessage.includes('Token mint') ||
        errorMessage.includes('failed to get token accounts') ||
        errorName.includes('SolanaJSONRPCError');

      if (isInvalidMintError) {
        // Invalid mint address - return 0 silently
        // Don't log as error since this is expected for invalid addresses
        return 0;
      }
      // For other errors, log but still return 0
      console.error(
        `Failed to get token balance for mint ${mintAddress.toString()}:`,
        error,
      );
      return 0;
    }
  }

  /**
   * Get or create associated token account
   */
  async getOrCreateAssociatedTokenAccount(
    payer: PublicKey,
    owner: PublicKey,
    mint: PublicKey,
  ): Promise<PublicKey> {
    try {
      // Check if associated token account exists
      const associatedTokenAccount = await this.getAssociatedTokenAddress(
        owner,
        mint,
      );

      const accountInfo = await this.connection.getAccountInfo(
        associatedTokenAccount,
      );
      if (accountInfo) {
        return associatedTokenAccount;
      }

      // Create associated token account
      const instruction = this.createAssociatedTokenAccountInstruction(
        payer,
        associatedTokenAccount,
        owner,
        mint,
      );

      const transaction = new Transaction().add(instruction);

      // Note: This would need to be signed and sent by the caller
      return associatedTokenAccount;
    } catch (error) {
      console.error('Failed to get or create associated token account:', error);
      throw error;
    }
  }

  /**
   * Get associated token account address
   */
  async getAssociatedTokenAddress(
    owner: PublicKey,
    mint: PublicKey,
  ): Promise<PublicKey> {
    const [associatedTokenAccount] = await PublicKey.findProgramAddress(
      [owner.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mint.toBuffer()],
      ASSOCIATED_TOKEN_PROGRAM_ID,
    );
    return associatedTokenAccount;
  }

  /**
   * Create associated token account instruction
   */
  createAssociatedTokenAccountInstruction(
    payer: PublicKey,
    associatedToken: PublicKey,
    owner: PublicKey,
    mint: PublicKey,
  ): TransactionInstruction {
    return {
      keys: [
        { pubkey: payer, isSigner: true, isWritable: true },
        { pubkey: associatedToken, isSigner: false, isWritable: true },
        { pubkey: owner, isSigner: false, isWritable: false },
        { pubkey: mint, isSigner: false, isWritable: false },
        { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
        { pubkey: PublicKey.default, isSigner: false, isWritable: false }, // System program
      ],
      programId: ASSOCIATED_TOKEN_PROGRAM_ID,
      data: Buffer.alloc(0),
    };
  }

  /**
   * Create transfer instruction for SPL tokens
   */
  createTransferInstruction(
    source: PublicKey,
    destination: PublicKey,
    owner: PublicKey,
    amount: number,
    decimals: number,
  ): TransactionInstruction {
    const transferAmount = new BN(amount * Math.pow(10, decimals));

    return {
      keys: [
        { pubkey: source, isSigner: false, isWritable: true },
        { pubkey: destination, isSigner: false, isWritable: true },
        { pubkey: owner, isSigner: true, isWritable: false },
      ],
      programId: TOKEN_PROGRAM_ID,
      data: Buffer.concat([
        Buffer.from([3]), // Transfer instruction
        transferAmount.toArrayLike(Buffer, 'le', 8),
      ]),
    };
  }

  /**
   * Create approve instruction for SPL tokens
   */
  createApproveInstruction(
    source: PublicKey,
    delegate: PublicKey,
    owner: PublicKey,
    amount: number,
    decimals: number,
  ): TransactionInstruction {
    const approveAmount = new BN(amount * Math.pow(10, decimals));

    return {
      keys: [
        { pubkey: source, isSigner: false, isWritable: true },
        { pubkey: delegate, isSigner: false, isWritable: false },
        { pubkey: owner, isSigner: true, isWritable: false },
      ],
      programId: TOKEN_PROGRAM_ID,
      data: Buffer.concat([
        Buffer.from([4]), // Approve instruction
        approveAmount.toArrayLike(Buffer, 'le', 8),
      ]),
    };
  }

  /**
   * Get common SPL tokens (similar to commonTokenOnMainnet/TestNet)
   */
  getCommonSPLTokens(): Record<string, SPLTokenInfo> {
    return {
      USDC: {
        mint: new PublicKey('EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v'), // USDC on mainnet
        symbol: 'USDC',
        name: 'USD Coin',
        decimals: 6,
      },
      USDT: {
        mint: new PublicKey('Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB'), // USDT on mainnet
        symbol: 'USDT',
        name: 'Tether USD',
        decimals: 6,
      },
      RAY: {
        mint: new PublicKey('4k3Dyjzvzp8eMZWUXbBCjEvwSkkk59S5iCNLY3QrkX6R'), // RAY on mainnet
        symbol: 'RAY',
        name: 'Raydium',
        decimals: 6,
      },
      SRM: {
        mint: new PublicKey('SRMuApVNdxXokk5GT7XD5cUUgXMBCoAz2LHeuAoKWRt'), // SRM on mainnet
        symbol: 'SRM',
        name: 'Serum',
        decimals: 6,
      },
    };
  }

  /**
   * Get devnet common SPL tokens
   */
  getCommonSPLTokensDevnet(): Record<string, SPLTokenInfo> {
    return {
      USDC: {
        mint: new PublicKey('4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU'), // USDC on devnet
        symbol: 'USDC',
        name: 'USD Coin (Devnet)',
        decimals: 6,
      },
      USDT: {
        mint: new PublicKey('Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB'), // USDT on devnet
        symbol: 'USDT',
        name: 'Tether USD (Devnet)',
        decimals: 6,
      },
    };
  }
}

// Export singleton instance
export const createSolanaSPLToken = (connection: Connection) =>
  new SolanaSPLToken(connection);
